package com.evoapps.evoshift.domain.market.payroll.poland

/**
 * Polish payroll parameters for one calendar year. Contribution rates have been stable for years;
 * the amounts that change every year are the minimum wage, the minimum hourly rate for civil-law
 * contracts and the annual cap on pension/disability contributions (30 × the forecast average wage).
 *
 * Sources (2026): Rozporządzenie RM z 11.09.2025, Dz.U. 2025 poz. 1242 (minimum wage 4806 zł,
 * hourly 31,40 zł); obwieszczenie MRPiPS on the 2026 cap (282 600 zł = 30 × 9 420 zł);
 * (2027): Rozporządzenie RM z 14.09.2026, Dz.U. 2026 poz. 1213 (minimum wage 4950 zł, hourly
 * 32,30 zł); the 2027 cap is not announced yet (obwieszczenie MRPiPS, usually in December), so
 * the 2026 cap stands in for it and [capAnnounced] is false;
 * minimum wage: ustawa z 10.10.2002 o minimalnym wynagrodzeniu za pracę (Dz.U. 2024 poz. 1773)
 * art. 6–8a; overtime: Kodeks pracy (Dz.U. 2026 poz. 1245) art. 151¹, art. 151¹¹;
 * ustawa o systemie ubezpieczeń społecznych art. 16, 19, 22; ustawa o PIT art. 22 ust. 2 and 9,
 * art. 27, art. 21 ust. 1 pkt 148; ustawa o świadczeniach opieki zdrowotnej art. 79 and 81.
 *
 * SH-57 (leave and PPK), unchanged in 2025–2027:
 * sick pay: Kodeks pracy art. 92 § 1 (80%; 100% for an illness in pregnancy or after an accident
 * on the way to or from work; for 33 days a year, 14 after the year the person turned 50, then
 * the ZUS sickness benefit) and ustawa z 25.06.1999 o świadczeniach pieniężnych z ubezpieczenia
 * społecznego w razie choroby i macierzyństwa art. 11 (80% / 100%), art. 36 (base: the average
 * monthly pay of the 12 months before, less the employee's social contributions, 13.71%) and
 * art. 37 (a daily rate of 1/30 of the base); a salary is reduced by 1/30 for each day of illness
 * (Rozporządzenie MPiPS z 29.05.1996 w sprawie sposobu ustalania wynagrodzenia w okresie
 * niewykonywania pracy, § 11–12);
 * holiday pay: Kodeks pracy art. 152–154 (20 days, 26 after 10 years of work incl. school, pro rata
 * for part-time with a started day rounded up), art. 154² (a leave day is 8 hours), art. 172, and
 * Rozporządzenie MPiPS z 8.01.1997 (Dz.U. 1997 nr 2 poz. 14) § 6–9 (fixed components as if at
 * work, variable ones from the average of the 3 months before);
 * leave on demand: Kodeks pracy art. 167² (4 days of the annual leave a year); joiners and leavers:
 * art. 155¹ (pro rata to the months worked in the year, a started month counts as a whole one)
 * and art. 155² § 2 (a started day is rounded up) (SH-113);
 * absences shorten the working-time norm by 8 hours a day: Kodeks pracy art. 130 § 3;
 * PPK: ustawa z 4.10.2018 o pracowniczych planach kapitałowych art. 26 (employer 1.5%), art. 27
 * (employee 2%, or 0.5% on request when the month's income is at most 1.2 × the minimum wage),
 * art. 2 ust. 1 pkt 40 (the base: the pension contribution base without the annual cap); the
 * employer's contribution is the employee's taxable revenue (ustawa o PIT art. 12 ust. 1) and
 * carries no social or health contributions.
 */
data class PolandRates(
    val year: Int,
    val minimumWage: Double,
    val minimumHourlyRate: Double,
    /** Annual cap on the base of pension and disability contributions. */
    val pensionDisabilityCap: Double,
    /** False while the year's cap is not announced and the previous year's stands in for it. */
    val capAnnounced: Boolean = true,

    // Social insurance, as a fraction of the contribution base.
    val pensionEmployee: Double = 0.0976,
    val pensionEmployer: Double = 0.0976,
    val disabilityEmployee: Double = 0.015,
    val disabilityEmployer: Double = 0.065,
    val sicknessEmployee: Double = 0.0245,
    /** Fundusz Pracy (incl. Fundusz Solidarnościowy). */
    val labourFund: Double = 0.0245,
    /** Fundusz Gwarantowanych Świadczeń Pracowniczych. */
    val guaranteeFund: Double = 0.001,
    /** Health insurance, from the gross minus the person's social contributions. */
    val health: Double = 0.09,

    // Income tax (skala podatkowa).
    val taxRate: Double = 0.12,
    val higherTaxRate: Double = 0.32,
    val higherTaxThreshold: Double = 120_000.0,
    /** 1/12 of the annual tax-reducing amount (3 600 zł), applied by the payer after PIT-2. */
    val monthlyTaxReduction: Double = 300.0,
    /** Monthly fixed costs of earning income on an employment contract (local / commuting). */
    val employmentCosts: Double = 250.0,
    val employmentCostsCommuting: Double = 300.0,
    /** Costs of earning income on civil-law and specific-task contracts, as a fraction. */
    val civilCostsRate: Double = 0.20,
    /** Annual revenue exempt from tax for people under 26. */
    val youthReliefLimit: Double = 85_528.0,
    /** Civil-law contracts up to this amount a month are taxed at a flat rate (art. 30 ust. 1 pkt 5a). */
    val flatRateTaxLimit: Double = 200.0,
    /** Night allowance: this fraction of the minimum-wage hourly rate (Kodeks pracy art. 151⁸). */
    val nightAllowanceRate: Double = 0.20,
    /** Overtime supplement on other days (Kodeks pracy art. 151¹ § 1 pkt 2). */
    val overtimeSupplement: Double = 0.50,
    /**
     * Overtime supplement at night, on Sundays and holidays and over the average weekly norm
     * (art. 151¹ § 1 pkt 1, § 2); also for Sunday and holiday work without another day off (art. 151¹¹ § 2, 3).
     */
    val overtimeSupplementHigh: Double = 1.00,

    // SH-57: sick pay and the ZUS sickness benefit.
    /** Sick pay as a fraction of the base (Kodeks pracy art. 92 § 1 pkt 1). */
    val sickPayRate: Double = 0.80,
    /** Pregnancy, an accident on the way to or from work (art. 92 § 1 pkt 2–3). */
    val sickPayFullRate: Double = 1.00,
    /** Days of illness a year the employer pays for, then ZUS pays the sickness benefit. */
    val employerSickDays: Int = 33,
    /** The same from the year after the person turned 50. */
    val employerSickDaysOver50: Int = 14,
    /** The base is a month's pay over this many days (ustawa zasiłkowa art. 37). */
    val sickDaysInBaseMonth: Int = 30,
    /** Months averaged for the sick-pay base (ustawa zasiłkowa art. 36). */
    val sickBaseMonths: Int = 12,

    // SH-57: annual leave and holiday pay.
    val leaveDays: Int = 20,
    val leaveDaysLongService: Int = 26,
    /** SH-113: of those, the days the person may take on demand (PL: urlop na żądanie, Kodeks pracy art. 167²). */
    val onDemandLeaveDays: Int = 4,
    /** One day of leave in hours (Kodeks pracy art. 154²), times the FTE. */
    val leaveDayHours: Double = 8.0,
    /** Months averaged for the variable pay in holiday pay (rozporządzenie z 8.01.1997 § 7–9). */
    val holidayAverageMonths: Int = 3,
    /** SH-223: force majeure is paid at half the pay (Kodeks pracy art. 148¹ § 1). */
    val forceMajeurePayRate: Double = 0.50,

    // SH-57: PPK (pracownicze plany kapitałowe).
    val ppkEmployee: Double = 0.02,
    val ppkEmployeeReduced: Double = 0.005,
    val ppkEmployer: Double = 0.015,
    /** The reduced employee rate is open up to this multiple of the minimum wage a month. */
    val ppkReducedIncomeFactor: Double = 1.2
) {
    /** The employee's social contributions on employment, deducted from the sick-pay base: 13.71%. */
    val employeeSocialRate: Double get() = pensionEmployee + disabilityEmployee + sicknessEmployee

    companion object {
        val KNOWN = listOf(
            PolandRates(year = 2025, minimumWage = 4666.0, minimumHourlyRate = 30.50, pensionDisabilityCap = 260_190.0),
            PolandRates(year = 2026, minimumWage = 4806.0, minimumHourlyRate = 31.40, pensionDisabilityCap = 282_600.0),
            PolandRates(year = 2027, minimumWage = 4950.0, minimumHourlyRate = 32.30, pensionDisabilityCap = 282_600.0, capAnnounced = false)
        )

        /** The year's rates, or the nearest known year (flagged by [isExact] false). */
        fun forYear(year: Int): PolandRates =
            KNOWN.lastOrNull { it.year <= year } ?: KNOWN.first()

        fun isExact(year: Int) = KNOWN.any { it.year == year && it.capAnnounced }
    }
}
